import { useMemo, useContext, useEffect, useCallback, FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { VirtualizedTable } from '@components/TableNew';
import { TableColumnsContext } from '@components/TableNew/TableColumns';
import TableSortingContext, { SortingOption } from '@components/TableNew/TableSorting';
import { getCopyTableData, formatCopyDataAsString } from '@components/TableNew/TableUtils';
import { GlobalContext } from '@components/App/App.context';
import PermissionOverlay from '@components/PermissionOverlay/PermissionOverlay';
import { useTopTrendsTableConfig } from './TopTrendsTableConfig';
import { Sort } from '@models';
import { useFilterSort } from '@core/hooks/useFilterSort';
import { TopTrendsDataItem, getTopTrendsCopyTableData } from '@core/apiHooks/useTopTrendsData';
import useRequestFlavorAccess from '@core/apiHooks/useRequestFlavorAccess';
import { addNotification } from '@actions/notifications.actions';
import { currentCountrySelector } from '@selectors/countries.selector';
import { useTopTrends } from './TopTrendsContext';

const sortingOptions: SortingOption[] = [
    { id: 'penetrationValue', name: 'penetrationValue', defaultDirection: Sort.Desc },
    { id: 'incidenceValue', name: 'incidenceValue', defaultDirection: Sort.Desc },
    { id: 'restaurantCount', name: 'restaurantCount', defaultDirection: Sort.Desc },
    { id: 'growthValue', name: 'growthValue', defaultDirection: Sort.Desc },
    { id: 'cagrValue', name: 'cagrValue', defaultDirection: Sort.Desc },
    { id: 'knowItValue', name: 'knowItValue', defaultDirection: Sort.Desc },
    { id: 'haveTriedItValue', name: 'haveTriedItValue', defaultDirection: Sort.Desc },
    { id: 'loveOrLikeItValue', name: 'loveOrLikeItValue', defaultDirection: Sort.Desc },
];

const TopTrendsTable: FC = () => {
    const {
        tableData: data,
        flavorData,
        hasFlavorData,
        hasFlavorPermission,
        selectedWords,
        isSelectable,
        hasTags,
        growthPeriod,
        years,
        isLoading,
        onGrowthPeriodChange,
        onWordSelect,
        onSettingsClick,
        onDrillClick,
        onMenuExamplesClick,
        onCopyContentChange,
    } = useTopTrends();
    const dispatch = useDispatch();
    const { getRem } = useContext(GlobalContext);
    const currentCountry = useSelector(currentCountrySelector);
    const { requestAccess } = useRequestFlavorAccess();

    const showFlavorPermissionOverlay = useMemo(
        () => data.length > 0 && hasFlavorData && !hasFlavorPermission,
        [data.length, hasFlavorData, hasFlavorPermission]
    );

    const handleRequestAccessButtonClick = useCallback(() => {
        requestAccess(currentCountry, 'Top Trends');
        dispatch(addNotification({
            text: "Request sent",
            duration: 4000,
        }));
    }, [currentCountry, dispatch, requestAccess]);

    const {
        data: sortedData,
        sortingKey,
        sortingDirection,
        setSortingKey,
        setSortingDirection,
    } = useFilterSort<TopTrendsDataItem>(
        data,
        'penetrationValue',
        Sort.Desc,
    );

    const tableConfig = useTopTrendsTableConfig({
        data: sortedData,
        flavorData,
        hasFlavorData,
        hasFlavorPermission,
        selectedWords,
        isSelectable,
        hasTags,
        growthPeriod,
        years,
        onGrowthPeriodChange,
        onWordSelect,
        onSettingsClick,
        onDrillClick,
        onMenuExamplesClick,
    });

    const tableProviderValue = useMemo(
        () => ({ data: tableConfig }),
        [tableConfig]
    );

    const copyTableContent = useMemo(() => {
        if (sortedData.length === 0) return '';
        const baseData = getCopyTableData(tableConfig, sortedData.length);
        const copyData = getTopTrendsCopyTableData(baseData, growthPeriod);
        return formatCopyDataAsString(copyData);
    }, [tableConfig, sortedData.length, growthPeriod]);

    useEffect(() => {
        onCopyContentChange?.(copyTableContent);
    }, [copyTableContent, onCopyContentChange]);

    const sortContextValue = useMemo(
        () => ({
            value: {
                id: sortingKey as string,
                direction: sortingDirection,
            },
            options: sortingOptions,
            setValue: ({ id, direction }: { id: string | number; direction: Sort }) => {
                setSortingKey(id as keyof TopTrendsDataItem);
                setSortingDirection(direction);
            },
        }),
        [sortingKey, sortingDirection, setSortingKey, setSortingDirection]
    );

    const SKELETON_ROW_COUNT = 30;

    const displayRowCount = useMemo(() => {
        if (isLoading) {
            return SKELETON_ROW_COUNT;
        }
        return sortedData.length;
    }, [isLoading, sortedData.length]);

    return (
        <TableSortingContext.Provider value={sortContextValue}>
            <TableColumnsContext.Provider value={tableProviderValue}>
                <div className="TopTrendsTable">
                    <VirtualizedTable
                        isLoading={isLoading}
                        rowsCount={displayRowCount}
                        headHeight={getRem(55)}
                        rowHeight={getRem(50)}
                        showFallback
                        testId="topTrendsTable"
                    />
                    {showFlavorPermissionOverlay && (
                        <div className="TopTrendsTable__flavor-permission-overlay">
                            <PermissionOverlay
                                handleButtonClick={handleRequestAccessButtonClick}
                                variant={sortedData.length < 3 ? 'row narrow' : undefined}
                                hideIcon={sortedData.length <= 3}
                            />
                        </div>
                    )}
                </div>
            </TableColumnsContext.Provider>
        </TableSortingContext.Provider>
    );
};

export default TopTrendsTable;
